import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <div
        className="relative hidden flex-1 bg-slate-900 md:block"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,23,42,0.78), rgba(15,23,42,0.92)), url('https://images.pexels.com/photos/236748/pexels-photo-236748.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/logo-wb.png" alt="West Brasil" className="h-12 w-12 rounded-md object-cover" />
            <span className="text-xl font-bold tracking-tight">WEST BRASIL</span>
          </div>
          <div>
            <h1 className="text-4xl font-bold leading-tight tracking-tight">
              Sistema da West
            </h1>
            <p className="mt-4 max-w-md text-lg text-slate-300">
              Controle de estoque de tecido por rolo — cada quilo rastreado, do recebimento à produção.
            </p>
          </div>
          <div className="text-sm text-slate-400">Confecção • Brás, São Paulo</div>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center bg-slate-50 p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8 md:hidden">
            <div className="flex items-center gap-3">
              <img src="/logo-wb.png" alt="West Brasil" className="h-12 w-12 rounded-md object-cover" />
              <span className="text-xl font-bold tracking-tight text-slate-900">WEST BRASIL</span>
            </div>
          </div>

          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Entrar no sistema</h2>
          <p className="mt-1 text-sm text-slate-500">Acesse com seu usuário</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Usuário</label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                required
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="off"
                name="west-usuario"
                className="h-14 w-full rounded-md border-2 border-slate-300 px-4 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Digite seu usuário"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Senha</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                required
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-md bg-red-50 p-3 text-sm font-medium text-red-700" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-md bg-slate-900 text-base font-semibold text-white transition-colors hover:bg-slate-800 disabled:opacity-60"
          >
            {loading && <Loader2 className="animate-spin" size={18} />}
            Entrar
          </button>
        </form>
      </div>
    </div>
  );
}
